'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useGetProfileTabDataQuery } from 'src/api/redux/profiles';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { getProfileUrl } from 'src/utils/profileUrl';
import { MedHorizontalBarChart } from 'src/components/MedCharts';
import chartColorScale from 'src/modules/Profile/components/Charts/chartColorScale';
import type { HorizontalBarDatum } from 'src/components/MedCharts';

const MAX_ITEMS = 8;

const CompactOpenPayments: React.FC = () => {
  const { entityDetails } = useProfile();
  const { closeDrawer } = useDrawer();

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);

  const { data, isLoading } = useGetProfileTabDataQuery(
    {
      id: providerId,
      type: profileType,
      view: 'payments',
    },
    { skip: !providerId }
  );

  const chartData: HorizontalBarDatum[] = useMemo(() => {
    const stack = data?.charts?.stack;
    if (!stack?.series?.length) return [];

    const items: HorizontalBarDatum[] = stack.series.map(
      (series: { name: string; data: number[] }, i: number) => ({
        label: series.name,
        value: series.data.reduce(
          (sum: number, v: number) => sum + Number(v),
          0
        ),
        color: chartColorScale[i % chartColorScale.length],
      })
    );

    return items
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_ITEMS);
  }, [data]);

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-open-payments"
    >
      <WidgetHeader fallbackTitle="Open Payments" />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <WidgetLoading data-testid="compact-open-payments-loading" />
        ) : chartData.length === 0 ? (
          <WidgetEmpty data-testid="compact-open-payments-empty" variant="full" />
        ) : (
          <MedHorizontalBarChart
            data={chartData}
            maxItems={MAX_ITEMS}
            formatValue={formatCurrency}
          />
        )}
      </div>

      {chartData.length > 0 && (
        <Link
          href={getProfileUrl(profileType, providerId, 'payments')}
          onClick={() => closeDrawer('profile')}
          className="flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors"
          data-testid="compact-open-payments-view-all"
        >
          View all open payments
          <Icon icon={faArrowRight} className="h-3 w-3" />
        </Link>
      )}
    </Card>
  );
};

export default CompactOpenPayments;
